import axios from "axios";

const DEFAULT_API_BASE = "http://localhost:5050/v1/api";
const envValue = (...keys: string[]): string =>
  keys
    .map((key) => String(process.env[key] || "").trim())
    .find(Boolean) || "";
const ENV_API_BASE = String(
  envValue(
    "REACT_APP_API_BASE",
    "REACT_APP_API_BASE_URL",
    "REACT_APP_API_ORIGIN"
  ) ||
    DEFAULT_API_BASE
)
  .trim()
  .replace(/\/+$/, "");
const ENV_UPLOADS_BASE = String(
  envValue(
    "REACT_APP_UPLOADS_BASE_URL",
    "REACT_APP_UPLOADS_BASE",
    "REACT_APP_R2_PUBLIC_BASE_URL"
  )
)
  .trim()
  .replace(/\/+$/, "");
const UPLOAD_BUCKET_PREFIXES = ["products", "contents", "orders"];
const ENV_UPLOADS_ORIGIN = (() => {
  if (!ENV_UPLOADS_BASE) return "";
  try {
    return new URL(ENV_UPLOADS_BASE).origin.toLowerCase();
  } catch {
    return "";
  }
})();

const isLoopbackHost = (host: string): boolean =>
  host === "localhost" || host === "127.0.0.1" || host === "::1";

const isPrivateIpv4Host = (host: string): boolean => {
  const parts = host.split(".");
  if (parts.length !== 4) return false;
  const nums = parts.map((part) => Number(part));
  if (nums.some((part) => !Number.isInteger(part) || part < 0 || part > 255)) {
    return false;
  }
  const [a, b] = nums;
  return (
    a === 10 ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168) ||
    (a === 169 && b === 254)
  );
};

function normalizeApiBase(value: string): string {
  const raw = String(value || "").trim();
  if (!raw) return DEFAULT_API_BASE;

  try {
    const parsed = new URL(raw);
    const pathname = parsed.pathname.replace(/\/+$/, "");
    parsed.pathname = pathname.endsWith("/v1/api")
      ? pathname
      : pathname
        ? `${pathname}/v1/api`
        : "/v1/api";
    parsed.search = "";
    parsed.hash = "";
    return parsed.toString().replace(/\/+$/, "");
  } catch {
    return raw.replace(/\/+$/, "");
  }
}

function resolveApiBase() {
  const normalizedBase = normalizeApiBase(ENV_API_BASE);
  if (typeof window === "undefined") return normalizedBase;

  try {
    const parsed = new URL(normalizedBase);
    const currentHost = window.location.hostname;
    const isLoopbackApiHost = isLoopbackHost(parsed.hostname);
    const isLoopbackCurrentHost = isLoopbackHost(currentHost);

    if (isLoopbackApiHost && isPrivateIpv4Host(currentHost)) {
      parsed.hostname = currentHost;
      return parsed.toString().replace(/\/+$/, "");
    }

    if (isPrivateIpv4Host(parsed.hostname)) {
      if (isLoopbackCurrentHost) {
        parsed.hostname = "localhost";
        return parsed.toString().replace(/\/+$/, "");
      }
      if (isPrivateIpv4Host(currentHost) && parsed.hostname !== currentHost) {
        parsed.hostname = currentHost;
        return parsed.toString().replace(/\/+$/, "");
      }
    }
  } catch {
    return normalizedBase;
  }

  return normalizedBase;
}

export const API_BASE = resolveApiBase();

export const http = axios.create({
  baseURL: API_BASE,
});

export function setHttpAuthToken(token: string | null | undefined) {
  const value = String(token || "").trim();
  if (!value) {
    delete http.defaults.headers.common.Authorization;
    return;
  }
  http.defaults.headers.common.Authorization = `Bearer ${value}`;
}

export function getHttpAuthHeader(): Record<string, string> {
  const value = http.defaults.headers.common.Authorization;
  if (typeof value === "string" && value.trim()) {
    return { Authorization: value.trim() };
  }
  return {};
}

// Determine the API origin once (e.g., http://localhost:5050)
export const API_ORIGIN = (() => {
  try {
    const u = new URL(API_BASE);
    return `${u.protocol}//${u.host}`;
  } catch {
    return DEFAULT_API_BASE.replace(/\/v1\/api$/, "");
  }
})();

const buildUploadUrl = (uploadKey: string): string => {
  const normalizedKey = uploadKey.replace(/^\/+/, "");
  const base = `${API_ORIGIN}/uploads`.replace(/\/+$/, "");
  return `${base}/${normalizedKey}`;
};

const buildConfiguredUploadUrl = (uploadKey: string): string => {
  const normalizedKey = uploadKey.replace(/^\/+/, "");
  const base = (ENV_UPLOADS_BASE || `${API_ORIGIN}/uploads`).replace(/\/+$/, "");
  return `${base}/${normalizedKey}`;
};

const pushUnique = (values: string[], value?: string) => {
  const normalized = String(value || "").trim();
  if (normalized && !values.includes(normalized)) values.push(normalized);
};

function extractUploadKey(raw: string): string | null {
  const value = String(raw || "").trim();
  if (!value) return null;

  if (/^r2:\/\//i.test(value)) {
    const withoutScheme = value.replace(/^r2:\/\//i, "");
    const slashIndex = withoutScheme.indexOf("/");
    if (slashIndex <= 0) return null;
    const key = withoutScheme.slice(slashIndex + 1).replace(/^\/+/, "");
    const firstSegment = key.split("/")[0];
    if (!UPLOAD_BUCKET_PREFIXES.includes(firstSegment)) return null;
    return UPLOAD_BUCKET_PREFIXES.includes(firstSegment) ? key : null;
  }

  let pathname = value;
  try {
    const parsed = new URL(value);
    const origin = parsed.origin.toLowerCase();
    pathname = parsed.pathname || value;

    const isKnownUploadHost =
      (!!ENV_UPLOADS_ORIGIN && origin === ENV_UPLOADS_ORIGIN) ||
      // Existing order snapshots retain this production asset hostname even
      // when the admin build has no uploads-base environment variable.
      parsed.hostname.toLowerCase() === "images.beyondpendants.com";
    const isBackendUploadPath =
      pathname.startsWith("/uploads/") || pathname.startsWith("/v1/api/uploads/");
    if (!isKnownUploadHost && !isBackendUploadPath) {
      return null;
    }
  } catch {
    // keep raw value
  }

  pathname = pathname.replace(/^\/v1\/api(?=\/uploads\/)/i, "");
  if (pathname.startsWith("/uploads/")) pathname = pathname.slice("/uploads/".length);
  else pathname = pathname.replace(/^\/+/, "");

  const normalized = pathname.replace(/\\/g, "/").trim();
  const firstSegment = normalized.split("/")[0];
  if (!UPLOAD_BUCKET_PREFIXES.includes(firstSegment)) return null;
  return normalized;
}

/** Turn "/uploads/123.jpg" (or a disk path) into a browser-usable URL */
export function assetUrl(u?: string): string | undefined {
  if (!u) return undefined;
  const raw = String(u).trim();
  if (!raw) return undefined;
  if (/^(blob:|data:)/i.test(raw)) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  const uploadKey = extractUploadKey(raw);
  if (uploadKey) return buildUploadUrl(uploadKey);
  if (/^https?:\/\//i.test(raw)) {
    try {
      const absoluteUploadKey = extractUploadKey(raw);
      if (absoluteUploadKey) {
        return buildUploadUrl(absoluteUploadKey);
      }
      return raw;
    } catch {
      return raw;
    }
  }
  if (raw.startsWith("/v1/api/uploads/")) {
    const relativeUploadKey = extractUploadKey(raw);
    return relativeUploadKey ? buildUploadUrl(relativeUploadKey) : `${API_ORIGIN}${raw.replace(/^\/v1\/api/, "")}`;
  }
  if (raw.startsWith("/uploads/")) {
    const relativeUploadKey = extractUploadKey(raw);
    return relativeUploadKey ? buildUploadUrl(relativeUploadKey) : `${API_ORIGIN}${raw}`;
  }
  if (raw.startsWith("/")) return `${API_ORIGIN}${raw}`;
  if (/^uploads\//i.test(raw)) {
    const relativeUploadKey = extractUploadKey(raw);
    return relativeUploadKey ? buildUploadUrl(relativeUploadKey) : `${API_ORIGIN}/${raw}`;
  }

  const normalized = raw.replace(/\\/g, "/");
  const uploadsMarker = normalized.toLowerCase().indexOf("/uploads/");
  if (uploadsMarker >= 0) {
    const fromMarker = normalized.slice(uploadsMarker + "/uploads/".length);
    const markerUploadKey = extractUploadKey(fromMarker);
    return markerUploadKey
      ? buildUploadUrl(markerUploadKey)
      : `${API_ORIGIN}${normalized.slice(uploadsMarker)}`;
  }

  // If someone stored a disk path, only keep the file name
  const file = raw.split(/[\\/]/).pop() || raw;
  return buildUploadUrl(file);
}

export function assetUrlCandidates(u?: string): string[] {
  if (!u) return [];
  const raw = String(u).trim();
  if (!raw) return [];
  if (/^(blob:|data:)/i.test(raw)) return [raw];

  const candidates: string[] = [];
  const uploadKey = extractUploadKey(raw);
  if (uploadKey) {
    pushUnique(candidates, buildUploadUrl(uploadKey));
    pushUnique(candidates, buildConfiguredUploadUrl(uploadKey));
  }

  pushUnique(candidates, assetUrl(raw));
  if (/^https?:\/\//i.test(raw) || raw.startsWith("//")) {
    pushUnique(candidates, raw.startsWith("//") ? `https:${raw}` : raw);
  }

  return candidates;
}
